import React, { useState, useEffect } from 'react';
import { User, Mail, Lock, ShieldCheck, ArrowRight, Eye, EyeOff, RefreshCw, X } from 'lucide-react';
import { db } from '../firebase'; 
import { ref, set, get } from "firebase/database"; 
import { useNavigate } from 'react-router-dom'; // Auto-redirect ke liye
import Swal from 'sweetalert2'; // Professional Alerts
import './Register.css';

const Register = () => {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [captchaText, setCaptchaText] = useState("");
  const [showTerms, setShowTerms] = useState(false);
  
  // Form States
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [email, setEmail] = useState("");
  const [captchaInput, setCaptchaInput] = useState("");

  const generateCaptcha = () => {
    const numbers = "123456789"; 
    let result = "";
    for (let i = 0; i < 4; i++) {
      result += numbers.charAt(Math.floor(Math.random() * numbers.length));
    }
    setCaptchaText(result);
  };

  useEffect(() => {
    generateCaptcha();
  }, []);

  const handleRegister = async (e) => {
    e.preventDefault();

    // 1. Password Length Validation
    if (password.length < 6) {
      Swal.fire({
        icon: 'warning',
        title: 'Weak Password',
        text: 'Password must be at least 6 characters long!',
        confirmButtonColor: '#fbbf24'
      });
      return;
    }

    // 2. Captcha Validation
    if (captchaInput !== captchaText) {
      Swal.fire({
        icon: 'error',
        title: 'Invalid Captcha',
        text: 'Please enter the correct verification code.',
      });
      generateCaptcha();
      return;
    }

    try {
      // Show Loading
      Swal.fire({
        title: 'Processing...',
        didOpen: () => { Swal.showLoading() },
        allowOutsideClick: false
      });

      // 3. Check if Username already exists
      const userRef = ref(db, 'users/' + username);
      const snapshot = await get(userRef);
      
      if (snapshot.exists()) {
        Swal.fire({
          icon: 'error',
          title: 'Oops...',
          text: 'Username already taken! Try another one.',
        });
        return;
      }

      // 4. Save Data to Firebase
      await set(userRef, {
        username: username,
        email: email,
        password: password, 
        balance: 0,
        role: "user",
        createdAt: new Date().toISOString()
      });

      // 5. Success Alert & Redirect
      Swal.fire({
        icon: 'success',
        title: 'Registration Successful!',
        text: 'Welcome to the trading platform. Redirecting to login...',
        timer: 2500,
        showConfirmButton: false
      });

      // Clear Form & Redirect after 2.5 seconds
      setTimeout(() => {
        navigate('/login');
      }, 2500);

    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Database Error',
        text: error.message,
      });
    }
  };

  return (
    <div className="reg-container">
      <div className="reg-card">
        <div className="reg-header">
          <h1>Create Account</h1>
          <p>Start your professional trading journey with us today.</p>
        </div>

        <form className="reg-form" onSubmit={handleRegister}>
          <div className="input-group">
            <label>Username</label>
            <div className="input-wrapper">
              <User size={18} className="input-icon" />
              <input 
                type="text" 
                placeholder="Choose a username" 
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
              />
            </div>
          </div>

          <div className="input-group">
            <label>Password (Min. 6 chars)</label>
            <div className="input-wrapper">
              <Lock size={18} className="input-icon" />
              <input 
                type={showPassword ? "text" : "password"} 
                placeholder="Create a strong password" 
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
              <button type="button" className="eye-toggle" onClick={() => setShowPassword(!showPassword)}>
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
            {password.length > 0 && password.length < 6 && (
              <span className="error-text">Password is too short</span>
            )}
          </div>

          <div className="input-group">
            <label>Email Address</label>
            <div className="input-wrapper">
              <Mail size={18} className="input-icon" />
              <input 
                type="email" 
                placeholder="example@mail.com" 
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
          </div>

          <div className="input-group">
            <label>Verification Code</label>
            <div className="captcha-container">
              <div className="input-wrapper captcha-input">
                <ShieldCheck size={18} className="input-icon" />
                <input 
                  type="text" 
                  placeholder="Enter code" 
                  maxLength="4" 
                  value={captchaInput}
                  onChange={(e) => setCaptchaInput(e.target.value)}
                  required
                />
              </div>
              <div className="captcha-visual-modern" onClick={generateCaptcha}>
                <svg width="100" height="45" viewBox="0 0 100 45">
                  <defs>
                    <filter id="cleanDistort">
                      <feTurbulence type="turbulence" baseFrequency="0.03" numOctaves="1" result="noise" />
                      <feDisplacementMap in="SourceGraphic" in2="noise" scale="4" />
                    </filter>
                  </defs>
                  <rect width="100%" height="100%" fill="#f8fafc" rx="8" />
                  <text 
                    x="50%" y="65%" 
                    textAnchor="middle" 
                    className="captcha-text-main"
                    filter="url(#cleanDistort)"
                  >
                    {captchaText}
                  </text>
                </svg>
                <RefreshCw size={14} className="captcha-refresh" />
              </div>
            </div>
          </div>

          <div className="terms-check">
            <input type="checkbox" id="terms" required />
            <label htmlFor="terms">
              I agree to the <span onClick={() => setShowTerms(true)}>Terms & Conditions</span>
            </label>
          </div>

          <button type="submit" className="reg-btn">
            Register Now <ArrowRight size={18} />
          </button>
        </form>

        <div className="reg-footer">
          <p>Already have an account? <a href="/login">Login Here</a></p>
        </div>
      </div>

      {showTerms && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Trading Terms & Conditions</h3>
              <X className="close-icon" onClick={() => setShowTerms(false)} />
            </div>
            <div className="modal-body">
              <p>1. <strong>Investment Risk:</strong> Trading involves high risk.</p>
              <p>2. <strong>Verification:</strong> Valid email is mandatory.</p>
              <p>3. <strong>Fair Play:</strong> Multi-accounting is banned.</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Register;